<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ImportInvoices',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const file = ref(null);
  const formId = ref(null);
  const formName = ref('ImportInvoices');
  const result = ref(null);
  const formats = ref([
    {
      value: 'szamlazz_hu_csv',
      text: $fn.value.pgettext('ImportInvoicesFormat', 'Szamlazz.hu CSV'),
      accept: '.csv',
    },
    {
      value: 'ubl_xml',
      text: $fn.value.pgettext('ImportInvoicesFormat', 'UBL XML'),
      accept: '.xml',
    },
  ]);
  const format = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_4823665167724729_validation = computed(() => [
    'ext:xlsx',
    'required',
  ]);

  const $data = computed(() => ({
    file: file.value,
    formId: formId.value,
    formName: formName.value,
    result: result.value,
    formats: formats.value,
    format: format.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_4823665167724729_validation: b_4823665167724729_validation.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_format = () => {
    return formats.value[0].value;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'file' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'result' },
      { name: 'formats' },
      { name: 'format' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_4823665167724729_validation', nodeId: '4823665167724729' },
    ],
    loaders: { formId: $$load_formId, format: $$load_format },
    ux: {},
    dataRefs: {
      file,
      formId,
      formName,
      result,
      formats,
      format,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_4823665167724729_validation,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('ImportInvoices|Title', 'Import Invoices'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_f0bja998d_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_648747096704084_setData_c9uy6a109_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'result',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_648747096704084_request_2yom12fal_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_invoices_from_file',
      data: { file: file.value, format: format.value },
      contentType: 'multipart/form-data',
    };
  };
  const act_2434514588044777_closeModal_imwtogqcl_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_f0bja998d =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_f0bja998d_getActionArgs,
    });
  const inst_evh_7315092382398562_close_w8vv6kbni =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_f0bja998d,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_w8vv6kbni],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_8wxsn4kdl =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_r16p72631 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_8wxsn4kdl,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_r16p72631],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_s9vtw4hja =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_wxi53nkn6 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_s9vtw4hja,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_wxi53nkn6],
    event: 'close',
  });
  const act_648747096704084_setData_c9uy6a109 = new actionTypes.SetDataAction({
    actionArgs: act_648747096704084_setData_c9uy6a109_getActionArgs,
  });
  const inst_evh_648747096704084_success_7vv11whyq =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_setData_c9uy6a109,
      event: 'success',
      displayName: 'setData',
    });
  const act_648747096704084_request_2yom12fal = new actionTypes.RequestAction({
    actionArgs: act_648747096704084_request_2yom12fal_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_648747096704084_success_7vv11whyq],
  });
  const inst_evh_648747096704084_submit_p8o7qtwwp =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_request_2yom12fal,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_648747096704084_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_648747096704084_submit_p8o7qtwwp],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_imwtogqcl =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_imwtogqcl_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_4tyhxu6e4 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_imwtogqcl,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_4tyhxu6e4],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_w8vv6kbni,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_r16p72631,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_wxi53nkn6,
    inst_evh_8564662037462133_close,
    inst_evh_648747096704084_success_7vv11whyq,
    inst_evh_648747096704084_submit_p8o7qtwwp,
    submit: inst_evh_648747096704084_submit_p8o7qtwwp,
    inst_evh_648747096704084_submit,
    inst_evh_2434514588044777_clickPreventStop_4tyhxu6e4,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_w8vv6kbni = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_w8vv6kbni.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_r16p72631 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_r16p72631.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_wxi53nkn6 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_wxi53nkn6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_success_7vv11whyq = async (event, scope) => {
    return await actions.inst_evh_648747096704084_success_7vv11whyq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_submit_p8o7qtwwp = async (event, scope) => {
    return await actions.inst_evh_648747096704084_submit_p8o7qtwwp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_submit = async (event, scope) => {
    return await actions.inst_evh_648747096704084_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_4tyhxu6e4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_4tyhxu6e4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ImportInvoices"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpAlert variant="info" show>
        <ItpIcon fa="info-circle"> </ItpIcon>
        <ItpText wrap>
          A funkció külső állományokból importál számlákat.
        </ItpText>
      </ItpAlert>
      <hr />
      <div class="container">
        <ItpForm
          :name="formName"
          :id="formId"
          v-if="loaded"
          @submit="evh_648747096704084_submit($event, {})"
        >
          <ItpFormGroup
            name="format"
            :label="pgettext('ImportInvoices', 'File format')"
            labelFor="format"
            required
          >
            <ItpFormNativeSelect
              v-model="format"
              name="format"
              required
              :options="formats"
            >
            </ItpFormNativeSelect>
          </ItpFormGroup>
          <ItpFormGroup
            name="file"
            :label="pgettext('ImportInvoices', 'Export file')"
            labelFor="file"
            :description="
              pgettext(
                'ImportInvoices',
                'The file can be obtained from the respective providers\'s application.'
              )
            "
            required
            v-if="format"
          >
            <ItpFormFile
              v-model="file"
              name="file"
              :accept="format.accept"
              required
              :validation="b_4823665167724729_validation"
            >
            </ItpFormFile>
          </ItpFormGroup>
        </ItpForm>
        <ItpAlert show v-if="result && result.message">
          <span>
            {{ result.message }}
          </span>
        </ItpAlert>
        <ItpFormTextarea
          name="details"
          v-model="result.details"
          monospace
          readonly
          v-if="result && result.details"
        >
        </ItpFormTextarea>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="formId"
          :text="pgettext('ImportInvoices', 'Submit file')"
          type="submit"
          variant="primary"
          icon="fa-file-import"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
